import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  Appointment, CreateCustomerInput, CreateEstimateInput, CreatePropertyInput, Customer, Equipment,
  Estimate, Invoice, Job, Lead, Membership, Message, Paginated, Payment, Property, User,
} from '@worksy360/shared';
import { api } from './api.js';

export interface DashboardData {
  jobsNeedingScheduling: Job[];
  estimatesAwaitingResponse: Estimate[];
  overdueInvoices: Invoice[];
}
export interface Timeline {
  customer: Customer;
  estimates: Estimate[];
  jobs: Job[];
  invoices: Invoice[];
  payments: Payment[];
}
export interface SearchResults {
  byPhone?: { cid: string; displayName: string; value: string }[];
  byEmail?: { cid: string; displayName: string; value: string }[];
  byName?: Customer[];
}

export const useMe = () => useQuery({ queryKey: ['me'], queryFn: () => api.get<{ auth: unknown; user: User | null }>('/me') });

export const useDashboard = () =>
  useQuery({ queryKey: ['dashboard'], queryFn: () => api.get<DashboardData>('/dashboard') });

export const useCustomers = () =>
  useQuery({ queryKey: ['customers'], queryFn: () => api.get<Paginated<Customer>>('/customers') });

export const useCustomer = (id: string) =>
  useQuery({ queryKey: ['customer', id], queryFn: () => api.get<Customer>(`/customers/${id}`), enabled: !!id });

export const useTimeline = (id: string) =>
  useQuery({ queryKey: ['timeline', id], queryFn: () => api.get<Timeline>(`/customers/${id}/timeline`), enabled: !!id });

export const useLeads = (stage: string) =>
  useQuery({ queryKey: ['leads', stage], queryFn: () => api.get<Paginated<Lead>>(`/leads?stage=${stage}`) });

export function useSearch(term: string) {
  return useQuery({
    queryKey: ['search', term],
    queryFn: () => api.get<SearchResults>(`/search?q=${encodeURIComponent(term)}`),
    enabled: term.trim().length >= 2,
  });
}

export function useCreateCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateCustomerInput) => api.post<Customer>('/customers', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['customers'] }),
  });
}

/** Invalidate a customer's timeline + dashboard after any money-loop action. */
export function useRefreshCustomer(customerId: string) {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: ['timeline', customerId] });
    qc.invalidateQueries({ queryKey: ['dashboard'] });
    qc.invalidateQueries({ queryKey: ['customers'] });
  };
}

export const useProperties = (id: string) =>
  useQuery({ queryKey: ['properties', id], queryFn: () => api.get<{ items: Property[] }>(`/customers/${id}/properties`), enabled: !!id });

export const useWebsite = () =>
  useQuery({ queryKey: ['website'], queryFn: () => api.get<import('@worksy360/shared').Website | null>('/website') });

export const useEquipment = (cid: string) =>
  useQuery({ queryKey: ['equipment', cid], queryFn: () => api.get<{ items: Equipment[] }>(`/customers/${cid}/equipment`), enabled: !!cid });
export const useMemberships = (cid: string) =>
  useQuery({ queryKey: ['memberships', cid], queryFn: () => api.get<{ items: Membership[] }>(`/customers/${cid}/memberships`), enabled: !!cid });

export const useInbox = (state: 'open' | 'all') =>
  useQuery({ queryKey: ['inbox', state], queryFn: () => api.get<Paginated<Message>>(`/inbox?state=${state}`) });

export const useThread = (cid: string) =>
  useQuery({ queryKey: ['thread', cid], queryFn: () => api.get<{ items: Message[] }>(`/customers/${cid}/messages`), enabled: !!cid });

export function useSendMessage(cid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { channel: 'sms' | 'note' | 'email'; body: string }) => api.post<Message>(`/customers/${cid}/messages`, body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['thread', cid] }); qc.invalidateQueries({ queryKey: ['inbox'] }); },
  });
}

export interface TechAssignment { aid: string; jobId: string; customerId: string; title: string; start: string; end: string; status: string }
export const useMyDay = (userId: string, date: string) =>
  useQuery({
    queryKey: ['myday', userId, date],
    queryFn: () => api.get<{ items: TechAssignment[] }>(`/schedule/tech/${userId}?from=${date}T00:00:00.000Z&to=${date}T23:59:59.999Z`),
    enabled: !!userId,
  });

export const useJob = (cid: string, jid: string) =>
  useQuery({ queryKey: ['job', cid, jid], queryFn: () => api.get<Job>(`/customers/${cid}/jobs/${jid}`), enabled: !!cid && !!jid });

export const useSchedule = (date: string) =>
  useQuery({ queryKey: ['schedule', date], queryFn: () => api.get<{ items: Appointment[] }>(`/schedule?date=${date}`), enabled: !!date });

export const useUnassignedJobs = () =>
  useQuery({ queryKey: ['jobs', 'needs_scheduling'], queryFn: () => api.get<Paginated<Job>>('/jobs?status=needs_scheduling') });

export function useCreateProperty(customerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Omit<CreatePropertyInput, 'customerId'>) => api.post<Property>(`/customers/${customerId}/properties`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['properties', customerId] }),
  });
}

export function useCreateEstimate(customerId: string) {
  const refresh = useRefreshCustomer(customerId);
  return useMutation({
    mutationFn: (input: Omit<CreateEstimateInput, 'customerId'>) =>
      api.post<Estimate>(`/customers/${customerId}/estimates`, input),
    onSuccess: refresh,
  });
}
